import React, { useState, useEffect } from "react";
import { Box, Typography, makeStyles, Chip, Tabs, Tab } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { fetchPageInvestigation, getInvestigationList } from "store/slices/investigation";

const useStyles = makeStyles(() => ({
  container: {
    padding: "20px",
    backgroundColor: "#F5F5F3",
  },
  titleRow: {
    marginBottom: "14px",
  },
  mainTitle: {
    fontSize: "20px",
    fontWeight: 500,
    marginBottom: "4px",
    color: "#1a1a18",
  },
  subtitle: {
    fontSize: "12px",
    color: "#888780",
  },
  tableCard: {
    background: "#fff",
    borderRadius: "12px",
    border: "0.5px solid #e5e5e2",
    overflow: "hidden",
  },
  tabContainer: {
    borderBottom: "0.5px solid #e5e5e2",
    "& .MuiTab-root": {
      fontSize: "12px",
      fontWeight: 500,
      color: "#888780",
      padding: "9px 16px",
      textTransform: "none",
      minHeight: "auto",
      "&.Mui-selected": {
        color: "#1a1a18",
        fontWeight: 500,
      },
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#1a1a18",
      height: "2px",
    },
  },
  tabContent: {
    padding: "14px",
  },
  table: {
    width: "100%",
    minWidth: "1000px",
    borderCollapse: "collapse",
    fontSize: "13px",
    tableLayout: "fixed",
  },
  th: {
    textAlign: "left",
    padding: "8px 0",
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    borderBottom: "0.5px solid #e5e5e2",
  },
  td: {
    padding: "10px 0",
    color: "#1a1a18",
    borderBottom: "0.5px solid #F1EFE8",
    fontSize: "12px",
  },
  overdueRow: {
    background: "#FFF5F5",
  },
  badgeRed: {
    background: "#FEE2E2",
    color: "#991b1b",
  },
  badgeGreen: {
    background: "#ccfbf1",
    color: "#0f766e",
  },
  badgeAmber: {
    background: "#FEF3C7",
    color: "#92400E",
  },
  badgeUnderReview: {
    background: "#FEF3C7",
    color: "#92400E",
  },
  badgeBlue: {
    background: "#DBEAFE",
    color: "#1E40AF",
  },
  badgeGray: {
    background: "#F1EFE8",
    color: "#5f5e5a",
  },
}));

const InvestigationTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const investigations = useSelector(getInvestigationList);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    dispatch(fetchPageInvestigation({ page: 1 }));
  }, [dispatch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  const handleRowClick = (item) => {
    history.push(`/admin/investigations/${item.id}`);
  };

  const formatIncidentType = (item) => {
    if (item.incidentTypes && item.incidentTypes.length) {
      return item.incidentTypes
        .map((t) => (t || "").toString().replace(/_/g, " ").toLowerCase())
        .join(", ");
    }
    return item.incidentTypeOther || "-";
  };

  const getSeverityBadgeClass = (severity) => {
    switch ((severity || "").toUpperCase()) {
      case "LOW":
        return classes.badgeBlue;
      case "MODERATE":
      case "SERIOUS":
        return classes.badgeAmber;
      case "NOTIFIABLE":
        return classes.badgeRed;
      default:
        return classes.badgeGray;
    }
  };

  const getInvestigationStatusLabel = (item) => {
    if (item.investigation || item.investigationStatus) {
      return item.investigation || item.investigationStatus;
    }
    const steps = [
      item.rootCauseStepStatus,
      item.correctiveActionStepStatus,
      item.evidenceStepStatus,
      item.directorSignOffStepStatus,
    ];
    if (steps.every((s) => s === "COMPLETE")) {
      return "Complete";
    }
    if (steps.some((s) => s === "IN_PROGRESS" || s === "COMPLETE")) {
      return "In progress";
    }
    return "Not started";
  };

  const getInvestigationBadgeClass = (status) => {
    switch (status) {
      case "Not started":
        return classes.badgeBlue;
      case "In progress":
        return classes.badgeBlue;
      case "Complete":
        return classes.badgeGreen;
      default:
        return classes.badgeGray;
    }
  };

  const getRowClass = (item) => {
    if (item.status === "Open") {
      return classes.overdueRow;
    }
    return "";
  };

  const tabs = [
    { label: "Incident register" },
    { label: "Monthly report" },
  ];

  return (
    <Box className={classes.container}>
      <Box className={classes.titleRow}>
        <Typography className={classes.mainTitle}>
          Incidents &amp; investigations
        </Typography>
        <Typography className={classes.subtitle}>
          Incident log → investigation → root cause → corrective action → director sign-off
        </Typography>
      </Box>

      <Box className={classes.tableCard}>
        <Tabs
          className={classes.tabContainer}
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>

        {activeTab === 0 && (
          <Box className={classes.tabContent}>
            <Box style={{ overflowX: "auto" }}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th} style={{ width: "8%" }}>Ref</th>
                    <th className={classes.th} style={{ width: "10%" }}>Date</th>
                    <th className={classes.th} style={{ width: "14%" }}>Participant</th>
                    <th className={classes.th} style={{ width: "13%" }}>House</th>
                    <th className={classes.th} style={{ width: "15%" }}>Type</th>
                    <th className={classes.th} style={{ width: "10%" }}>Severity</th>
                    <th className={classes.th} style={{ width: "10%" }}>Investigation</th>
                    <th className={classes.th} style={{ width: "10%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {investigations && investigations.length > 0 ? (
                    investigations.map((item) => (
                      <tr
                        key={item.id}
                        className={getRowClass(item)}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(item)}
                      >
                        <td className={classes.td} style={{ fontWeight: 700, padding: '0 3px' }}>
                          {item.incidentNumber || `INC-${item.id}`}
                        </td>
                        <td className={classes.td}>{formatDate(item.createdAt || item.date)}</td>
                        <td className={classes.td}>
                          {item.participantId ? `Participant #${item.participantId}` : "-"}
                        </td>
                        <td className={classes.td}>{item.house || item.location}</td>
                        <td className={classes.td} style={{ textTransform: "capitalize" }}>
                          {formatIncidentType(item)}
                        </td>
                        <td className={classes.td}>
                          <Chip
                            size="small"
                            label={item.severity}
                            className={getSeverityBadgeClass(item.severity)}
                            style={{ padding: '0 3px' }}
                          />
                        </td>
                        <td className={classes.td}>
                          <Chip
                            size="small"
                            label={getInvestigationStatusLabel(item)}
                            className={getInvestigationBadgeClass(getInvestigationStatusLabel(item))}
                            style={{ padding: '0 3px' }}
                          />
                        </td>
                        <td className={classes.td}>
                          <AppButton
                            label={item.status === "Closed" ? "View" : "Investigate"}
                            style={{
                              fontSize: "11px",
                              padding: "4px 10px",
                              borderRadius: "5px",
                              backgroundColor: '#fff',
                              border: `0.5px solid ${item.status === "Closed" ? '#5f5e5a' : AppColor.mainColor}`,
                              color: item.status === "Closed" ? '#5f5e5a' : AppColor.mainColor,
                              height: '24px',
                              minHeight: '24px'
                            }}
                            buttonColor="transparent"
                            rounder
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(item);
                            }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "24px", color: "#888780" }}>
                        No investigations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box className={classes.tabContent}>
            features under development
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InvestigationTable;
